import { formatNumber, formatEnumLabel } from '../utils/format';
import { tickToGameDate, formatDay } from '../utils/gameTime';
import { hasAnyGain, formatGainLabel, buildCombatNarrative } from '../utils/attack';
import { formatDuration } from '../utils/formatDuration';
import type { AttackResult as AttackResultType } from '../types/attack';
import { COLORS } from '../constants/theme';

interface AttackResultProps {
  result: AttackResultType;
  currentTick: number;
  tickIntervalMs: number;
}

export function AttackResult({ result, currentTick, tickIntervalMs }: AttackResultProps) {
  const isVictory = result.outcome === 'VICTORY';

  const bannerStyle = isVictory
    ? { backgroundColor: COLORS.successBg, borderColor: COLORS.successBorder, color: COLORS.successText }
    : { backgroundColor: COLORS.errorBg, borderColor: COLORS.errorBorder, color: COLORS.errorText };

  return (
    <div
      role="alert"
      aria-live={isVictory ? 'polite' : 'assertive'}
      className="border px-4 py-3 rounded"
      style={bannerStyle}
    >
      {/* Outcome Header */}
      <div className="text-xl font-bold mb-3">
        {isVictory ? '🏆 VICTORY' : '💀 DEFEAT'}
        {result.attackType && (
          <span className="text-base font-normal ml-2">— {formatEnumLabel(result.attackType)}</span>
        )}
      </div>

      {/* SF-180 Component 4: combat report narrative, extracted to buildCombatNarrative() so this
          component's own diff stays a single line insertion. */}
      <p className="text-sm mb-3">{buildCombatNarrative(result)}</p>

      {/* Combat Summary */}
      <div className="mb-3 pb-3" style={{ borderBottom: `1px solid ${COLORS.border}` }}>
        <div className="font-medium mb-2">Combat Summary</div>
        <div className="text-sm space-y-1">
          <div>Attacker Offense: {formatNumber(result.attackerOffense)} OP</div>
          <div>Defender Defense: {formatNumber(result.defenderDefense)} DP</div>
          <div>
            Offense Ratio: {(result.attackerOffense / result.defenderDefense).toFixed(3)}{' '}
            {/* SF-180: WIN/LOSS label now matches result.outcome directly — the previous re-derived
                `× 0.97` threshold was removed engine-side by SF-89 and could label a DEFEAT "(WIN)". */}
            <span className={isVictory ? 'text-green-400' : 'text-red-400'}>
              ({isVictory ? 'WIN' : 'LOSS'})
            </span>
          </div>
        </div>
      </div>

      {/* Gains (Victory Only) */}
      {isVictory && (
        <div className="mb-3 pb-3" style={{ borderBottom: `1px solid ${COLORS.border}` }}>
          <div className="font-medium mb-2">Gains</div>
          <div className="text-sm space-y-1">
            {result.landCaptured > 0 && (
              <div className="text-yellow-400">
                Land Captured: {formatNumber(result.landCaptured)} acres
              </div>
            )}
            {result.prisonersCaptured > 0 && (
              <div className="text-yellow-400">
                Prisoners Captured: {formatNumber(result.prisonersCaptured)}
              </div>
            )}
            {Object.entries(result.resourcesStolen ?? {}).map(([res, amt]) =>
              amt > 0 ? (
                <div key={res} className="text-yellow-400">
                  {formatGainLabel('resources', res)}: {formatNumber(amt)}
                </div>
              ) : null
            )}
            {Object.entries(result.scienceStolen ?? {}).map(([cat, amt]) =>
              amt > 0 ? (
                <div key={cat} className="text-yellow-400">
                  {formatGainLabel('science', cat)}: {formatNumber(amt)}
                </div>
              ) : null
            )}
            {Object.entries(result.unitsMassacred ?? {}).map(([unit, count]) =>
              count > 0 ? (
                <div key={unit} className="text-red-400">
                  {formatGainLabel('massacre', unit)}: {formatNumber(count)}
                </div>
              ) : null
            )}
            {(result.landDestroyed ?? 0) > 0 && (
              <div className="text-red-400">
                Land Destroyed: {formatNumber(result.landDestroyed)} acres
              </div>
            )}
            {!hasAnyGain(result) && (
              <div style={{ color: COLORS.text.tertiary }}>None</div>
            )}
          </div>
        </div>
      )}

      {/* Losses */}
      <div className="mb-3 pb-3" style={{ borderBottom: `1px solid ${COLORS.border}` }}>
        <div className="font-medium mb-2">Losses</div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {/* Attacker Losses */}
          <div>
            <div className="text-xs mb-1" style={{ color: COLORS.text.tertiary }}>Your Army</div>
            <UnitLosses losses={result.attackerLosses} />
          </div>

          {/* Defender Losses */}
          <div>
            <div className="text-xs mb-1" style={{ color: COLORS.text.tertiary }}>Enemy Army</div>
            <UnitLosses losses={result.defenderLosses} />
          </div>
        </div>
      </div>

      {/* Timing */}
      <div className="text-sm space-y-1">
        <div className="font-medium mb-1">Timing</div>
        <div>
          Travel Time: {formatDuration(Math.ceil(result.attackTime), {
            returnTimeFraction: result.attackTime - Math.floor(result.attackTime),
            tickIntervalMs,
          })}
        </div>
        {(() => {
          const remaining = result.returnTime - currentTick;
          if (remaining <= 0) return null;
          const returnDate = tickToGameDate(Math.floor(result.returnTime));
          const countdown = formatDuration(Math.ceil(remaining), {
            returnTimeFraction: remaining - Math.floor(remaining),
            tickIntervalMs,
            includeMinutes: true,
          });
          return (
            <div className="text-xs mt-1" style={{ color: COLORS.text.tertiary }}>
              {`Army returns: ${returnDate.month} ${formatDay(returnDate.day)}, YR${returnDate.year} (in ${countdown})`}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

interface UnitLossesProps {
  losses: Record<string, number> | null;
}

function UnitLosses({ losses }: UnitLossesProps) {
  if (!losses) {
    return <div className="text-xs" style={{ color: COLORS.text.tertiary }}>None</div>;
  }
  const hasLosses = Object.values(losses).some((count) => count > 0);

  if (!hasLosses) {
    return <div className="text-xs" style={{ color: COLORS.text.tertiary }}>None</div>;
  }

  return (
    <div className="space-y-1 text-xs">
      {Object.entries(losses).map(([unitType, count]) => {
        if (count === 0) return null;

        // Format unit type name
        const displayName = unitType
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');

        return (
          <div key={unitType} className="text-red-400">
            {displayName}: {formatNumber(count)}
          </div>
        );
      })}
    </div>
  );
}
